import { Check, ContentCopy, ThumbUp, ThumbDown } from "@mui/icons-material";
import { Box, CircularProgress } from "@mui/material";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { memo, ReactNode, useEffect, useState } from "react";
import { Grid, Typography, IconButton } from "src/lib/components/basics";
import { StyledMessage } from "./styled";
import { IChatMessageProps } from "./types";
import { setMessageFeedback } from "src/services/api/chat";
import { useSnackbarStore } from "src/store/snackbarStore";
import { ClarificationQuestions } from "./ClarificationQuestions";
import { MessageBubble } from "./MessageBubble";
import { MessageFooter } from "./MessageFooter";

const markdownComponents = {
    p: ({ ...childProps }: any) => <div {...childProps} />,
    code: ({ ...childProps }: any) => <span {...childProps} />,
    pre: ({ ...childProps }: any) => <span {...childProps} />,
    table: ({ children, ...rest }: any) => <table {...rest}>{children}</table>,
    thead: ({ children, ...rest }: any) => <thead {...rest}>{children}</thead>,
    tbody: ({ children, ...rest }: any) => <tbody {...rest}>{children}</tbody>,
    tr: ({ children, ...rest }: any) => <tr {...rest}>{children}</tr>,
    th: ({ children, ...rest }: any) => <th {...rest}>{children}</th>,
    td: ({ children, ...rest }: any) => <td {...rest}>{children}</td>,
};

const STREAM_STATUS_MIN_HEIGHT = "1.75rem";

const StreamingPipelineStatus = ({ streamStatus }: { streamStatus: string }) => (
    <Box
        className="chat-streaming-status"
        sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            minHeight: STREAM_STATUS_MIN_HEIGHT,
            mb: 0.5,
        }}
    >
        <CircularProgress size={16} color="primary" aria-hidden />
        <Typography variant="body2" color="primary" sx={{ fontStyle: "italic", opacity: 0.9 }}>
            {streamStatus}
        </Typography>
    </Box>
);

const streamingTextSx = {
    whiteSpace: "pre-wrap" as const,
    wordBreak: "break-word" as const,
    lineHeight: 1.6,
};

const MessageBody = ({
    text,
    status,
    streamStatus,
    plain,
}: {
    text: string;
    status?: IChatMessageProps["status"];
    streamStatus?: string;
    plain?: boolean;
}) => {
    if (status === "streaming") {
        const showPipelineStatus = !!streamStatus?.trim() && !text.trim();

        return (
            <Box className={plain ? "chat-assistant-body" : undefined}>
                {showPipelineStatus ? <StreamingPipelineStatus streamStatus={streamStatus!.trim()} /> : null}
                {text ? (
                    <Typography variant="body2" color="primary" sx={streamingTextSx}>
                        {text}
                    </Typography>
                ) : null}
            </Box>
        );
    }

    const body = (
        <Markdown skipHtml={false} remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {text}
        </Markdown>
    );

    if (plain) {
        return <Box className="chat-assistant-body">{body}</Box>;
    }

    return body;
};

const actionsRowSx = {
    // MUI's Grid container always ships with width: 100% — inline-flex alone doesn't override that
    // explicit value, so it has to be reset here too for the row to actually hug its icon buttons.
    display: "inline-flex",
    width: "auto",
    mt: 0.75,
    minHeight: 32,
};

const COPY_CONFIRMATION_MS = 2000;

const CopyButton = ({ onCopy }: { onCopy: () => void }) => {
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!copied) return;
        const timeoutId = window.setTimeout(() => setCopied(false), COPY_CONFIRMATION_MS);
        return () => window.clearTimeout(timeoutId);
    }, [copied]);

    return (
        <IconButton
            onClick={() => {
                onCopy();
                setCopied(true);
            }}
            variant="ghost"
            color="primary"
            size="small"
        >
            {copied ? <Check /> : <ContentCopy />}
        </IconButton>
    );
};

const FeedbackButtons = ({
    onFeedback,
    disabled,
}: {
    onFeedback: (liked: boolean) => void;
    disabled: boolean;
}) => (
    <>
        <IconButton onClick={() => onFeedback(true)} variant="ghost" color="primary" size="small" disabled={disabled}>
            <ThumbUp />
        </IconButton>
        <IconButton onClick={() => onFeedback(false)} variant="ghost" color="primary" size="small" disabled={disabled}>
            <ThumbDown />
        </IconButton>
    </>
);

/** Just a row for icon buttons — which buttons, and in what order, is entirely up to the caller. */
const ActionsRow = ({
    reserveSpace,
    children,
}: {
    reserveSpace: boolean;
    children: ReactNode;
}) => (
    <Grid
        container
        alignItems="center"
        gap={0.5}
        sx={{ ...actionsRowSx, visibility: reserveSpace ? "hidden" : "visible" }}
        aria-hidden={reserveSpace}
    >
        {children}
    </Grid>
);

const MessageTimestamp = ({ timestamp }: { timestamp?: string }) => (
    <Typography variant="caption" color="quaternary" sx={{ fontSize: "0.6875rem" }}>
        {timestamp}
    </Typography>
);

const Message = memo(function Message(props: IChatMessageProps) {
    const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
    const showSnackbar = useSnackbarStore((state) => state.showSnackbar);

    const messageText = props.text ?? props.children?.toString() ?? "";
    const isAssistant = !!props.answer;

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(messageText);
        } catch (err) {
            console.error("Erro ao copiar texto:", err);
        }
    };

    const handleFeedback = async (liked: boolean) => {
        if (!props.answer || !props.jsonMessage || !props.questionText) return;

        try {
            setIsSubmittingFeedback(true);
            await setMessageFeedback({
                answer: props.jsonMessage.natural_language_response || messageText,
                question: props.questionText,
                cypher_query: props.jsonMessage.cypher_query || "",
                liked: liked ? "true" : "false",
            });
            showSnackbar(
                liked ? "Feedback positivo enviado com sucesso!" : "Feedback negativo enviado com sucesso!",
                "success"
            );
        } catch (error) {
            console.error("Erro ao enviar feedback:", error);
            showSnackbar("Erro ao enviar feedback. Tente novamente.", "error");
        } finally {
            setIsSubmittingFeedback(false);
        }
    };

    const canGiveFeedback: boolean = Boolean(
        props.answer &&
            props.status !== "streaming" &&
            props.jsonMessage &&
            props.questionText
    );
    const reserveActionSpace: boolean = !!props.answer && props.status === "streaming";

    const body = (
        <MessageBody
            text={messageText}
            status={props.status}
            streamStatus={props.streamStatus}
            plain={isAssistant}
        />
    );

    if (isAssistant) {
        return (
            <StyledMessage container answer plainAnswer>
                <Grid item sx={{ width: "100%", maxWidth: "100%" }}>
                    <MessageBubble align="start" withBackground={false}>
                        {body}
                    </MessageBubble>
                    {props.jsonMessage?.clarification && props.status !== "streaming" ? (
                        <ClarificationQuestions
                            round={props.jsonMessage.clarification.round}
                            questions={props.jsonMessage.clarification.questions}
                        />
                    ) : null}
                    <MessageFooter justify="flex-start">
                        <ActionsRow reserveSpace={reserveActionSpace}>
                            <CopyButton onCopy={handleCopy} />
                            {canGiveFeedback ? (
                                <FeedbackButtons onFeedback={handleFeedback} disabled={isSubmittingFeedback} />
                            ) : null}
                        </ActionsRow>
                        <MessageTimestamp timestamp={props.status !== "streaming" ? props.timestamp : undefined} />
                    </MessageFooter>
                </Grid>
            </StyledMessage>
        );
    }

    return (
        <StyledMessage container answer={props.answer} context={!!props.context}>
            <Grid item sx={{ minWidth: 0, maxWidth: "100%" }}>
                <MessageBubble align="end" contextLabel={props.context} attachments={props.attachments}>
                    <Typography variant="body2">{body}</Typography>
                </MessageBubble>
                <MessageFooter justify="flex-end">
                    <MessageTimestamp timestamp={props.timestamp} />
                    <ActionsRow reserveSpace={reserveActionSpace}>
                        <CopyButton onCopy={handleCopy} />
                    </ActionsRow>
                </MessageFooter>
            </Grid>
        </StyledMessage>
    );
});

export { Message };
